import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { clientQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import { GetClientResponse } from "@/types/response/GetClientResponse";

/**
 * Hook to fetch a single client by ID.
 *
 * The query is automatically disabled if no clientId is provided.
 *
 * @param clientId - The unique identifier of the client to fetch
 *
 * @returns React Query result containing client data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: client, isLoading } = useClient("client-123");
 * ```
 */
export function useClient(clientId?: string | null) {
  return useQuery<GetClientResponse, AxiosError>({
    queryKey: clientQueryKeys.detail(clientId ?? ""),
    queryFn: () =>
      instance
        .get<GetClientResponse>(`/api/v2/Clients/${clientId}`)
        .then((res) => res.data),
    enabled: !!clientId,
    refetchOnMount: true,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
  });
}
